import { useLayoutEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';

/**
 * Calque « zones pouce » : viewports étroits uniquement (max 719px).
 * Hauteur en dvh + sync visualViewport : évite le décalage où 100vh / inset:0
 * inclut la barre d’adresse (layout viewport « trop haut ») et pousse la zone verte hors écran.
 */
const $ThumbZoneOverlay = styled.div<{ $visible: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  height: 100dvh;
  max-height: 100dvh;
  box-sizing: border-box;
  z-index: 50;
  pointer-events: none;
  overflow: hidden;
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  visibility: ${({ $visible }) => ($visible ? 'visible' : 'hidden')};
  transition: opacity 0.22s ease, visibility 0.22s ease;

  @media (min-width: 720px) {
    display: none;
  }
`;

const $ThumbLayerRed = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 27%;
  background: rgba(220, 38, 38, 0.14);
`;

const $ThumbLayerOrange = styled.div`
  position: absolute;
  top: 27%;
  left: 0;
  right: 0;
  height: 40%;
  background: rgba(234, 88, 12, 0.14);
`;

const $ThumbLayerGreen = styled.div`
  position: absolute;
  top: 67%;
  left: 0;
  right: 0;
  height: 33%;
  background: rgba(22, 163, 74, 0.14);
`;

export interface ThumbZoneOverlayProps {
  visible: boolean;
}

export const ThumbZoneOverlay = ({ visible }: ThumbZoneOverlayProps) => {
  const thumbOverlayRef = useRef<HTMLDivElement | null>(null);

  useLayoutEffect(() => {
    if (!visible) {
      return undefined;
    }

    const vv = window.visualViewport;

    if (!vv) {
      return undefined;
    }

    const el = thumbOverlayRef.current;

    if (!el) {
      return undefined;
    }

    const applyVisualViewport = () => {
      el.style.setProperty('height', `${vv.height}px`);
      el.style.setProperty('width', `${vv.width}px`);
      el.style.setProperty('top', `${vv.offsetTop}px`);
      el.style.setProperty('left', `${vv.offsetLeft}px`);
    };

    applyVisualViewport();
    vv.addEventListener('resize', applyVisualViewport);
    vv.addEventListener('scroll', applyVisualViewport);

    return () => {
      vv.removeEventListener('resize', applyVisualViewport);
      vv.removeEventListener('scroll', applyVisualViewport);
      el.style.removeProperty('height');
      el.style.removeProperty('width');
      el.style.removeProperty('top');
      el.style.removeProperty('left');
    };
  }, [visible]);

  if (typeof document === 'undefined') {
    return null;
  }

  return createPortal(
    <$ThumbZoneOverlay ref={thumbOverlayRef} $visible={visible}>
      <$ThumbLayerRed />
      <$ThumbLayerOrange />
      <$ThumbLayerGreen />
    </$ThumbZoneOverlay>,
    document.body,
  );
};

ThumbZoneOverlay.displayName = 'ThumbZoneOverlay';
